import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { ReportView } from '../../../components/ui';
import { fetcher } from '../../../services/fetcher';
import {
  VesselOperationsReport,
  type VesselOperationsReportData,
} from './VesselOperationsReport';
import { useManagementCompanyLogoBlobUrl } from '../../wrh/reports/useManagementCompanyLogo';

function monthShortLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleString('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

const VesselOperationsReportPage: React.FC = () => {
  const { colors } = useTheme();
  const [params] = useSearchParams();
  const vesselId = params.get('vesselId')?.trim() || '';
  const month = params.get('month')?.trim() || '';

  const key =
    vesselId && month
      ? `/vessel-operations/sheet?vesselId=${encodeURIComponent(vesselId)}&month=${encodeURIComponent(month)}`
      : null;
  const { data, error, isLoading } = useSWR<VesselOperationsReportData>(key, fetcher, {
    revalidateOnFocus: false,
  });
  const { logoBlobUrl: companyLogoSrc } = useManagementCompanyLogoBlobUrl(
    data?.managementCompany?.id,
    data?.managementCompany?.logoStorageKey,
  );

  const backHref = useMemo(() => {
    const q = new URLSearchParams();
    if (vesselId) q.set('vesselId', vesselId);
    if (month) q.set('month', month);
    const s = q.toString();
    return s ? `/vessel-operations?${s}` : '/vessel-operations';
  }, [vesselId, month]);

  if (!vesselId || !month) {
    return (
      <div className={cn('flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4', colors.background)}>
        <p className={cn('text-sm', colors.textMuted)}>
          Open the vessel operations schedule and choose vessel / month first.
        </p>
        <Link to="/vessel-operations" className={cn('text-sm font-medium', colors.primaryFg, 'hover:underline')}>
          ← Back to schedule
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        Loading…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={cn('flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4', colors.background)}>
        <p className={cn('text-sm', colors.textMuted)}>Could not load vessel operations schedule.</p>
        <Link to={backHref} className={cn('text-sm font-medium', colors.primaryFg, 'hover:underline')}>
          ← Back to schedule
        </Link>
      </div>
    );
  }

  const empty = !data.days.length;

  return (
    <ReportView
      backTo={backHref}
      backLabel="Back to schedule"
      title="Vessel operations"
      subtitle={`${data.vesselName} · ${monthShortLabel(data.month)}`}
      printPageSize="A4 landscape"
      printMargin="0"
      empty={empty}
      emptyMessage="No days found for this vessel / month."
    >
      <VesselOperationsReport data={data} companyLogoSrc={companyLogoSrc} />
    </ReportView>
  );
};

export default VesselOperationsReportPage;

